import React, {useEffect, useState} from 'react';
import {
    Container,
    Typography,
    Box,
    Stack,
    TextField, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button
} from '@mui/material';
import {useAppSelector, useAppDispatch} from '../../app/hooks';
import {
    actionsCell,
    editButton, editIcon,
    searchField,
    searchInner,
    tableCell,
    tableContainer,
    tableHeaderCell
} from "./clientsStyles";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import TablesPagination from "../../components/TablesPagination/TablesPagination";
import {getClients, IClient, setItemsPerPage, setPageNumber} from "./clientsSlice";
import {createGetUsersQuery} from "../../helpers/createGetUsersQuery";
import { useSortingAndSearch } from './hooks/useSortingAndSearch';
import ClientForm from "./components/ClientForm";


interface ITableHeaders {
    id: keyof IClient;
    label: string
}

const tableHeaders: ITableHeaders[] = [
    {id: 'name', label: 'Name'},
    {id: 'socialMedia', label: 'Social Media'},
    {id: 'contact', label: 'Contact'}
];


const ClientsPage: React.FC = () => {
    const [isOpenForm, setIsOpenForm] = useState<boolean>(false);
    const [editingClient, setEditingClient] = useState<IClient | null>(null);
    const {sorting, searchValue, setSearchValue, handleSorting} = useSortingAndSearch();
    const dispatch = useAppDispatch();
    const {clients, totalCount, pageNumber, itemsPerPage} = useAppSelector((state) => state.clients);


    useEffect(() => {
        const queryParams = createGetUsersQuery(
            pageNumber,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getClients(queryParams));
    }, []);


    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        dispatch(setPageNumber(newPage))

        const queryParams = createGetUsersQuery(
            newPage + 1,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getClients(queryParams));
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setPageNumber(0));
        dispatch(setItemsPerPage(+event.target.value))

        const queryParams = createGetUsersQuery(
            1,
            +event.target.value,
            sorting,
            searchValue
        )
        dispatch(getClients(queryParams));
    }

    const handleEditClient = (currentClient: IClient) => {
        setEditingClient(currentClient);
        setIsOpenForm(true);
    }

    const onCloseForm = () => {
        setEditingClient(null);
        setIsOpenForm(false);
    }

    return (
        <Container sx={{mt: 4}}>
            {isOpenForm &&
                <ClientForm
                    editingClient={editingClient}
                    onCloseForm={onCloseForm}
                />
            }

            <Typography variant="h4" gutterBottom>
                Clients
            </Typography>
            <Box sx={{mt: 4}}>
                <Box sx={searchInner}>
                    <Stack spacing={2} sx={{width: 300}}>
                        <TextField
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            label="Search"
                            sx={searchField}
                        />
                    </Stack>
                    <Button onClick={()=>setIsOpenForm(true)}>
                        Create
                    </Button>
                </Box>
                <Paper sx={{width: '100%', overflow: 'hidden'}}>
                    <TableContainer sx={tableContainer}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map(header => (
                                        <TableCell
                                            sx={(theme) => ({
                                                backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                                ...tableHeaderCell,
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark' ? '#90caf9' : '#9cbed9',
                                                },
                                            })}
                                            key={header.id}
                                            onClick={() => handleSorting(header.id)}
                                        >
                                            {header.label}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        sx={(theme) => ({
                                            backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                            ...actionsCell
                                        })}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.map(client => {
                                    return (
                                        <TableRow key={client.id}>
                                            <TableCell sx={{...tableCell}}>{client.name}</TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    client.socialMedia &&
                                                    <Box>
                                                        {client.socialMedia.facebook &&
                                                            <p>facebook: {client.socialMedia.facebook}</p>
                                                        }
                                                        {client.socialMedia.linkedIn &&
                                                            <p>linkedin: {client.socialMedia.linkedIn}</p>
                                                        }
                                                        {client.socialMedia.comments &&
                                                            <p>comments: {client.socialMedia.comments}</p>
                                                        }
                                                    </Box>
                                                }
                                            </TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    client.contact &&
                                                    <Box>
                                                        <p>
                                                            {client.contact.country}
                                                            {client.contact.hometown && `,${client.contact.hometown}`}
                                                        </p>
                                                        <p>{client.contact.telephon}</p>
                                                        <p>{client.contact.email}</p>
                                                    </Box>
                                                }
                                            </TableCell>

                                            <TableCell sx={{...tableCell, textAlign: "center"}}
                                            >
                                                <Button
                                                    sx={editButton}
                                                    onClick={() => handleEditClient(client)}
                                                >
                                                    <BorderColorIcon sx={editIcon}/>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablesPagination
                        totalCount={totalCount}
                        itemsPerPage={itemsPerPage}
                        pageNumber={pageNumber}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default ClientsPage;
